import { useEffect, useMemo, useState } from "react";
import { moNum } from "@/lib/moNum";
import { ClipboardCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiErrorDetail } from "@/lib/api";
import { buildOrderBOM, buildAvailability } from "@/lib/orderBom";
import { toast } from "sonner";

const num = (v) => Number(v) || 0;

// Inline MO creation popover (same fields as the Manufacturing Orders creator).
export default function CreateMOPopover({ products, components, initialProductId = "", initialCfgId = "", initialDesignId = "", onPlaced, testId, projected = false, triggerLabel = "" }) {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState(initialProductId);
  const [cfgId, setCfgId] = useState(initialCfgId);
  const [designId, setDesignId] = useState(initialDesignId);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("bottles");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [price, setPrice] = useState(() => {
    const p = products.find((x) => x.id === initialProductId);
    return p?.default_price_per_case != null ? String(p.default_price_per_case) : "";
  });
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && customers.length === 0) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const product = products.find((p) => p.id === productId) || null;
  const designs = useMemo(() => {
    const seen = new Set();
    const out = [];
    (product?.lines || []).forEach((l) => (l.designs || []).forEach((d) => { if (d?.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); } }));
    return out;
  }, [product]);
  const cfgs = product?.pack_configs || [];
  const cfg = cfgs.find((c) => c.id === cfgId) || cfgs[0] || null;
  const casePackSize = num(cfg?.bottles_per_case);
  const bpp = casePackSize && cfg?.cases_per_pallet ? casePackSize * cfg.cases_per_pallet : 0;
  const ppt = num(cfg?.pallets_per_truckload);
  const perUnit = unit === "bottles" ? 1 : unit === "cases" ? casePackSize : unit === "pallets" ? bpp : bpp * ppt;
  const totalBottles = num(qty) * perUnit;

  const onProduct = (v) => {
    const p = products.find((x) => x.id === v);
    setProductId(v);
    setCfgId(p?.pack_configs?.[0]?.id || "");
    setDesignId("");
    setPrice(p?.default_price_per_case != null ? String(p.default_price_per_case) : "");
  };

  const valid = product && casePackSize > 0 && num(qty) > 0 && !!date && (designs.length === 0 || !!designId);

  const placeOrder = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const { data: draft } = await api.post("/manufacturing-orders", {
        product_id: product.id, case_pack: casePackSize, config_id: cfg?.id || "",
        quantity: num(qty), unit, production_date: date, design_id: designId || null,
        price_per_case: price === "" ? null : Number(price),
        customer_id: customerId, customer_name: customers.find((c) => c.id === customerId)?.company_name || "",
      });
      const totalCases = casePackSize ? totalBottles / casePackSize : 0;
      const totalPallets = bpp ? totalBottles / bpp : 0;
      const totalTrucks = ppt ? totalPallets / ppt : 0;
      const bom = buildOrderBOM(product, components, { cfg, casePackSize, totalCases, totalPallets, totalTrucks, designId });
      const compIndex = Object.fromEntries(components.map((c) => [c.id, c]));
      let reservations = {};
      try { reservations = (await api.get("/components/reservations")).data.by_key || {}; } catch { /* non-blocking */ }
      const { data } = await api.post(`/manufacturing-orders/${draft.id}/place`, {
        bottle_size: product.name || "",
        ordered_bottles: totalBottles,
        bom_snapshot: bom,
        availability: buildAvailability(bom, compIndex, reservations),
      });
      if (projected) await api.post(`/manufacturing-orders/${draft.id}/projected`, { projected: true });
      toast.success(projected
        ? `Projected order ${moNum(data.mwo_number || "")} added — components reserved, excluded from the schedule.`
        : `MO ${moNum(data.mwo_number || "")} placed — ${Math.round(totalBottles).toLocaleString()} bottles scheduled. See Manufacturing Orders.`);
      setOpen(false);
      setQty("");
      onPlaced?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002FA7]"
          title="Create a Manufacturing Order for this product"
          data-testid={testId}
        >
          <ClipboardCheck className="h-3.5 w-3.5" /> {triggerLabel || "Create MO"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-3" data-testid={`${testId}-panel`}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Product</p>
        <div className="space-y-1.5">
          <Select value={productId || undefined} onValueChange={onProduct}>
            <SelectTrigger className="rounded-sm" data-testid={`${testId}-product`}><SelectValue placeholder="Select a product" /></SelectTrigger>
            <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-1.5">
            {designs.length > 0 ? (
              <Select value={designId || undefined} onValueChange={setDesignId}>
                <SelectTrigger className="rounded-sm text-slate-600" data-testid={`${testId}-design`}><SelectValue placeholder="Select a design" /></SelectTrigger>
                <SelectContent>{designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
              </Select>
            ) : (
              <Input readOnly value="" placeholder={product ? "—" : ""} className="rounded-sm bg-slate-50 text-slate-400" />
            )}
            {cfgs.length > 1 ? (
              <Select value={cfg?.id || undefined} onValueChange={setCfgId}>
                <SelectTrigger className="rounded-sm" data-testid={`${testId}-pack`}><SelectValue placeholder="Select pack" /></SelectTrigger>
                <SelectContent>{cfgs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case}-pack`}</SelectItem>))}</SelectContent>
              </Select>
            ) : (
              <Input readOnly value={casePackSize ? `${casePackSize}-pack` : ""} placeholder={product ? "—" : ""} className="rounded-sm bg-slate-50 text-slate-600" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Qty" className="rounded-sm" data-testid={`${testId}-qty`} />
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="rounded-sm" data-testid={`${testId}-unit`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bottles">Bottles</SelectItem>
                <SelectItem value="cases">Cases</SelectItem>
                <SelectItem value="pallets">Pallets</SelectItem>
                <SelectItem value="truckloads">Truckloads</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid={`${testId}-date`} />
          <div className="grid grid-cols-2 gap-1.5">
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
              <Input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Price/Case" className="rounded-sm pl-6" title="Price per case (shown on the Production Order document)" data-testid={`${testId}-price`} />
            </div>
            <Select value={customerId || undefined} onValueChange={(v) => setCustomerId(v === "__none" ? "" : v)}>
              <SelectTrigger className={"rounded-sm" + (customerId ? "" : " text-slate-400")} data-testid={`${testId}-customer`}><SelectValue placeholder="Produce for…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {totalBottles > 0 && casePackSize > 0 && (
            <p className="text-[11px] text-slate-500">= {Math.round(totalBottles).toLocaleString()} bottles{bpp ? ` · ${(Math.round((totalBottles / bpp) * 100) / 100).toLocaleString()} pallets` : ""}</p>
          )}
          <Button disabled={!valid || saving} onClick={placeOrder} className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`${testId}-place`}>
            {saving ? "Placing…" : "Place Order"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
